import { useDocumentLayout } from '@/contexts/document-layout-context'
import { useEditorCommandState } from '@/contexts/editor-command-state-context'
import { useEditorCommands } from '@/contexts/editor-commands-context'
import { useEditorSettings } from '@/contexts/editor-settings-context'
import type { MenubarInputs } from '../types/menubar-inputs'

/**
 * The menu's inputs, read from the editor contexts. Undo/Redo stay `undefined` when unavailable,
 * which is how the Edit menu disables them.
 */
export function useMenubarInputs(): MenubarInputs {
  const settings = useEditorSettings()
  const layout = useDocumentLayout()
  const commands = useEditorCommands()
  const state = useEditorCommandState()

  return {
    gridMode: settings.gridMode,
    snapToGrid: settings.snapToGrid,
    showGuides: settings.showGuides,
    showSmartGuides: settings.showSmartGuides,
    snapToGuides: settings.snapToGuides,
    showBleedBar: settings.showBleed,
    showTextLinking: settings.showTextLinking,
    showStickyNotes: settings.showStickyNotes,
    rulerUnit: settings.rulerUnit ?? 'pica',
    columns: settings.columns,
    rows: settings.rows,
    colGutter: settings.colGutter,
    rowGutter: settings.rowGutter,
    defaultGuttersLinked: settings.defaultGuttersLinked,
    isGuidesDialogOpen: settings.isGuidesDialogOpen,
    preferencesValues: settings.preferencesValues,
    pageInnerWidth: layout.pageInnerWidth,
    pageInnerHeight: layout.pageInnerHeight,
    onMenuToggle: commands.menuToggle,
    onClose: commands.closeEditor,
    onToggleFullPica: settings.toggleFullPica,
    onToggleHalfPica: settings.toggleHalfPica,
    onToggleSnapToGrid: settings.toggleSnapToGrid,
    onToggleShowGuides: settings.toggleShowGuides,
    onToggleShowSmartGuides: settings.toggleShowSmartGuides,
    onToggleSnapToGuides: settings.toggleSnapToGuides,
    onToggleShowBleedBar: settings.toggleShowBleedBar,
    onToggleShowTextLinking: settings.toggleShowTextLinking,
    onToggleShowStickyNotes: settings.toggleShowStickyNotes,
    onSetColumnsRows: settings.setColumnsRows,
    onGuidesDialogOpenChange: settings.setGuidesDialogOpen,
    onPreferencesChange: settings.setPreferences,
    onSelectAll: commands.selectAll,
    onSelectNone: commands.selectNone,
    onCheckSpelling: commands.checkSpelling,
    selectNoneDisabled: state.selectNoneDisabled,
    onUndo: state.canUndo ? commands.undo : undefined,
    onRedo: state.canRedo ? commands.redo : undefined,
    onCut: commands.cut,
    onCopy: commands.copy,
    onDuplicate: commands.duplicate,
    onPaste: commands.paste,
    onPasteInPlace: commands.pasteInPlace,
    onDelete: commands.deleteSelection,
    cutDisabled: state.cutDisabled,
    copyDisabled: state.copyDisabled,
    duplicateDisabled: state.duplicateDisabled,
    pasteDisabled: state.pasteDisabled,
    pasteInPlaceDisabled: state.pasteInPlaceDisabled,
    deleteDisabled: state.deleteDisabled,
    onGroupObjects: commands.group,
    onUngroupObjects: commands.ungroup,
    groupObjectsDisabled: state.groupDisabled,
    ungroupObjectsDisabled: state.ungroupDisabled,
    onLockSelected: commands.lockSelected,
    onUnlockSelected: commands.unlockSelected,
    onFreezeSelected: commands.freezeSelected,
    onUnfreezeSelected: commands.unfreezeSelected,
    onUnlockAll: commands.unlockAll,
    onUnfreezeAll: commands.unfreezeAll,
    lockSelectedDisabled: state.lockSelectedDisabled,
    unlockSelectedDisabled: state.unlockSelectedDisabled,
    freezeSelectedDisabled: state.freezeSelectedDisabled,
    unfreezeSelectedDisabled: state.unfreezeSelectedDisabled,
    unlockAllDisabled: state.unlockAllDisabled,
    unfreezeAllDisabled: state.unfreezeAllDisabled,
    showUnlockSelected: state.showUnlockSelected,
    showUnfreezeSelected: state.showUnfreezeSelected,
    convertTextToShapeDisabled: state.convertTextToShapeDisabled,
    onConvertTextToShape: commands.convertTextToShape,
    textOnPathDisabled: state.textOnPathDisabled,
    onTextOnPath: commands.textOnPath,
  }
}
